import { Navigate, useLocation } from "react-router-dom";
import { JSX, useEffect, useState } from "react";
import { useTokenContext } from "../context/tokenContext";
import LoadingSpinner from "../components/Ui/LoadingSpinner";

interface ProtectedRouteProps {
  children: JSX.Element;
  requiredRole?: "SuperAdmin" | "Admin" | "Partner" | "Customer"; // Ajoutez tous les rôles nécessaires
}

const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const location = useLocation();
  const { isLoading, isAuthenticated, hasInitialized, utc, utp, isAdmin, isSuperAdmin } = useTokenContext();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  // Vérifier l'authentification seulement si pas encore vérifié et que le contexte a fini d'initialiser
  useEffect(() => {    
    // On attend que le contexte ait fini d'initialiser avant de faire des vérifications
    if (hasInitialized && !hasCheckedAuth) {
      setHasCheckedAuth(true);
    }
  }, [hasInitialized, hasCheckedAuth, isAuthenticated, isLoading]);

  // Si le contexte est encore en train d'initialiser ET qu'on n'est pas authentifié, afficher le spinner
  // Si on est authentifié, on peut afficher le contenu même si isLoading est true (cas de refreshUserData)
  if (!hasInitialized || (isLoading && !isAuthenticated)) {
    return <LoadingSpinner size="large" text="Vérification de l'authentification..." />;
  }

  // Si pas authentifié, rediriger vers la page de login
  if (!isAuthenticated) {
    // Redirige vers la page de login avec l'emplacement actuel pour pouvoir y retourner après connexion
    return <Navigate to="/" state={{ from: location }} replace />;
  } 

  // Si un rôle spécifique est requis, vérifier que l'utilisateur a ce rôle
  if (requiredRole) {
    let hasRequiredRole = false;
    
    switch (requiredRole) {
      case "SuperAdmin":
        hasRequiredRole = isSuperAdmin === 1;
        break;
      case "Admin":
        hasRequiredRole = isAdmin === 1;
        break;
      case "Partner":
        hasRequiredRole = utp === 1;
        break;
      case "Customer":
        hasRequiredRole = utc === 1;
        break;
      default:
        hasRequiredRole = false;
    }
    
    if (!hasRequiredRole) {
      // Redirige vers la page d'erreur si l'utilisateur n'a pas le bon rôle
      return <Navigate to="/error" state={{ error: "unauthorized" }} replace />;
    }
  }

  // Si tout est OK, afficher le contenu protégé
  return children;
};

export default ProtectedRoute;